import React, { memo, useEffect, useState, useContext, useMemo } from 'react';
import { Card, Radio } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import Table from './components/Table';
import { useRequest } from 'ahooks';
import {
  fetchStudioOverallProjectsLiveInternalBreakdownData,
  fetchStudioOverallProjectsLiveIntelligenceBreakdownData,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import { context as Context } from '@gp/components/Context';
import { formatDateStr } from '@gp/utils/common';
import { SORT_REQ_MAP } from './constants';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import TriggerFoldBtn from '@/pages/globalpedia/components/TriggerFoldBtn';
import { toProjectDetailsByGameType } from '@gp/utils/project';
import useProjects from '@gp/pages/Pipeline/ProjectDetails/_hooks/useProjects';

interface BreakdownListPropsType {
  setSourceCard: React.Dispatch<
    React.SetStateAction<{
      loading: boolean;
      data: any;
    }>
  > | null;
}

const DEFAULT_PROPS: BreakdownListPropsType = {
  setSourceCard: null,
};

function BreakdownList(props: BreakdownListPropsType) {
  const { setSourceCard } = props;

  const [dataSegment, setDataSegment] = useState<'filter' | 'all'>('filter');
  const [triggerFoldExpand, setTriggerFoldExpand] = useState<boolean>(false);

  const { projects } = useProjects();

  const { filter, filterLoading }: StudioOverallProjectsLiveContextType = useContext(Context) || {};

  const [sorts, setSorts] = useState<SortBy<any>>({
    by: 'total_revenue',
    order: 'desc',
  });

  const {
    loading: internalLoading,
    data: internalData,
    run: internalRun,
  } = useRequest(fetchStudioOverallProjectsLiveInternalBreakdownData, {
    manual: true,
  });
  const {
    loading: intelligenceLoading,
    data: intelligenceData,
    run: intelligenceRun,
  } = useRequest(fetchStudioOverallProjectsLiveIntelligenceBreakdownData, {
    manual: true,
  });

  const loading = useMemo(
    () => internalLoading || intelligenceLoading,
    [internalLoading, intelligenceLoading],
  );
  const data = useMemo(
    () => (filter?.source === 'internal' ? internalData : intelligenceData),
    [loading],
  );
  const headerDateFormatted = formatDateStr(data?.header_date, 'YYYY-MM');

  // 筛选器变化调接口, 排序发生改变调用接口
  useEffect(() => {
    // if (filterLoading) return;
    const genreDropArr = filter?.genreMap
      ?.map((t) => {
        return t?.msg;
      })
      ?.filter((t) => Array.isArray(t));
    let genre: any[] = [];
    for (let i = 0; i < genreDropArr?.length; i++) {
      const t = genreDropArr?.[i];
      genre = [...genre, ...t];
    }
    if (filter?.source === 'internal') {
      // 取得genre名称
      const paramGenre = genre?.map((t) => t?.genre)?.filter((t) => t);
      // 取得id
      const paramSelectId = filter?.projectMap?.map((t) => t?.id);
      internalRun({
        bo: filter?.bo,
        genre: paramGenre,
        select_id: paramSelectId,
        yoy: true,
        mom: true,
        order: sorts?.order,
        order_metric: SORT_REQ_MAP?.[sorts?.by],
      });
    } else {
      intelligenceRun({
        internal: '',
        bo: filter?.bo,
        genre,
        project_ids: filter?.projectMap,
        order: sorts?.order,
        order_metric: sorts?.by,
      });
    }
  }, [filter, sorts?.by, sorts?.order /* filterLoading */]);

  // 为了提高接口速率Current Month Gross Revenue/YTM Gross Revenue/MAU三个卡片的数据从breakdown列表接口拿
  useEffect(() => {
    setSourceCard?.({
      loading,
      data: data?.key_metrics,
    });
  }, [loading]);

  const toGameDetail = (cvm: any, ...restParams: any) => {
    const { game_id: projectId, game_name: project, platform } = cvm || {};
    const params = {
      id: projectId as any,
      name: project,
      tab: 'gameDetail',
      platform,
    };
    return toProjectDetailsByGameType(params, ...restParams, projects);
  };

  return (
    <CardSlot
      className="dbg-studio-overall-projects-live-breakdown"
      title={
        <div className="dbg-studio-overall-projects-live-breakdown-title">
          Live Projects Breakdown
          {!!headerDateFormatted && (
            <span className="dbg-studio-overall-projects-live-breakdown-time">
              Data Time: {headerDateFormatted}
            </span>
          )}
        </div>
      }
      rightDom={
        <div className="dbg-breakdown-list-filter">
          {filter?.source !== 'internal' && (
            <div className="header-fold-btn">
              <TriggerFoldBtn
                contentText="Expand by platform"
                onClick={() => setTriggerFoldExpand(!triggerFoldExpand)}
              />
            </div>
          )}
          <Radio.Group
            className="dbg-breakdown-list-filter-radio"
            value={dataSegment}
            onChange={(v: string) => {
              setDataSegment(v as 'filter' | 'all');
            }}
          >
            <Radio name="filter">Display available data only</Radio>
            <Radio name="all">Display all data</Radio>
          </Radio.Group>
        </div>
      }
    >
      <Table
        loading={/* filterLoading || */ loading}
        isHideExpandButton={filter?.source === 'internal'}
        data={data}
        setSorts={setSorts}
        triggerFoldExpand={triggerFoldExpand}
        dataSegment={dataSegment}
        toGameDetail={toGameDetail}
        isNeedData={true}
      />
    </CardSlot>
  );
}

BreakdownList.defaultProps = DEFAULT_PROPS;

export default memo(BreakdownList);
